'use client'
import { MultiCitySearchForm } from '@/src/components/common/home/flightSearch/searchForm/components/MultiCity'
import { useSearchInputManager } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import useFormatDate from '@/src/hooks/date/useFormatDate'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import { JsonsData } from '@/src/types/flight'
import { getFormattedDate } from '@/src/utils/date'
import { SwipeableDrawer } from '@mui/material'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AirportSelector } from './components/airportSelector'
import { DateSelector } from './components/dateSelector'
import { TravelersSelector } from './components/travelersSelector'
import { SearchHeader } from './searchHeader'

interface SearchHeaderWrapperProps {
    srpResultsLoading?: boolean
    open: boolean
    onOpen: () => void
    onClose: () => void
    jsons: JsonsData | undefined
}

const SearchHeaderWrapper: React.FC<SearchHeaderWrapperProps> = ({
    srpResultsLoading,
    open,
    onOpen,
    onClose,
    jsons
}) => {
    const searchManager = useSearchInputManager(jsons, srpResultsLoading, undefined, 6)
    const {
        fromToInputsState,
        onAirportSelect,
        isRoundTrip,
        tripType,
        setTripType,
        range,
        rangeDispatch,
        flightClassState,
        travelersState,
        handleTravelerChange,
        handleFlightClassChange,
        updateSearchParams,
        updateMultiCitySearchParams,
        getAirportsFromQuery,
        canIncreaseAdults,
        canIncreaseChildren,
        canIncreaseInfants,
        canDecreaseAdults,
        canDecreaseChildren,
        canDecreaseInfants,
        handleSwap
    } = searchManager

    const { showError } = useThemedToast()
    const t = useTranslations('common')

    const [activeComponent, setActiveComponent] = useState<'from' | 'to'>('from')
    const [fromToDrawerOpen, setFromToDrawerOpen] = useState(false)
    const [travelersDrawerOpen, setTravelersDrawerOpen] = useState(false)
    const [dateDrawerOpen, setDateDrawerOpen] = useState(false)
    const [sameAirportError, setSameAirportError] = useState(false)

    const isMulticity = tripType === 'multicity'

    useEffect(() => {
        const fromValue = fromToInputsState.from.value as string
        const toValue = fromToInputsState.to.value as string

        if (fromValue && toValue) {
            const fromCode = fromValue.split(' - ')[0]
            const toCode = toValue.split(' - ')[0]
            setSameAirportError(fromCode === toCode && fromCode !== '')
        } else {
            setSameAirportError(false)
        }
    }, [fromToInputsState.from.value, fromToInputsState.to.value])

    useEffect(() => {
        if (!isMulticity && sameAirportError) {
            showError(t('sameAirportError'))
        }
    }, [isMulticity, sameAirportError, showError, t])

    const handleTripTypeChange = useCallback(
        (type: 'oneway' | 'roundtrip' | 'multicity') => {
            setTripType(type)
            setFromToDrawerOpen(false)
            setDateDrawerOpen(false)
            setTravelersDrawerOpen(false)
        },
        [setTripType]
    )

    const { formatDate } = useFormatDate()

    const formatDateDisplay = useCallback(
        (date: Date | null) => {
            if (!date) return 'Select Date'
            return formatDate(getFormattedDate(date, 'MM/DD/YYYY'), 'ddd, DD MMM')
        },
        [formatDate]
    )

    const formatTravelersDisplay = useCallback(() => {
        const total = travelersState.adults + travelersState.childs + travelersState.infants
        return `${total} ${t('traveller', { s: total > 1 ? 's' : '' })}`
    }, [travelersState, t])

    const handleFromClick = useCallback(() => {
        setFromToDrawerOpen(true)
        setActiveComponent('from')
    }, [])

    const handleToClick = useCallback(() => {
        setFromToDrawerOpen(true)
        setActiveComponent('to')
    }, [])

    const handleDateClick = useCallback(() => {
        setDateDrawerOpen(true)
    }, [])

    const handleTravelersClick = useCallback(() => {
        setTravelersDrawerOpen(true)
    }, [])

    const handleMulticitySearch = useCallback(() => {
        updateMultiCitySearchParams()
        onClose()
    }, [updateMultiCitySearchParams, onClose])

    const handleSearch = useCallback(() => {
        if (sameAirportError) {
            return
        }
        updateSearchParams()
        onClose()
    }, [updateSearchParams, sameAirportError, onClose])

    const multicityContent = useMemo(
        () => (
            <MultiCitySearchForm
                searchManager={searchManager}
                showError={showError}
                onSearch={handleMulticitySearch}
                searchButtonLabel={t('modifySearch')}
                searchButtonVariant="customVariantPrimary"
                searchButtonDisabled={!!srpResultsLoading}
                showNonStop={false}
                compactTypography
            />
        ),
        [searchManager, showError, handleMulticitySearch, t, srpResultsLoading]
    )

    return (
        <>
            <SearchHeader
                open={open}
                onOpen={onOpen}
                onClose={onClose}
                tripType={tripType}
                onTripTypeChange={handleTripTypeChange}
                multicityContent={multicityContent}
                fromLocation={fromToInputsState.from.value}
                toLocation={fromToInputsState.to.value}
                departDate={formatDateDisplay(range?.from || null)}
                returnDate={isRoundTrip ? formatDateDisplay(range?.to || null) : undefined}
                travelers={formatTravelersDisplay()}
                flightClass={Array.isArray(flightClassState) ? flightClassState[0] : flightClassState}
                onFromClick={handleFromClick}
                onToClick={handleToClick}
                onDateClick={handleDateClick}
                onTravelersClick={handleTravelersClick}
                onSearch={handleSearch}
                onSwap={handleSwap}
                hasError={sameAirportError}
                srpResultsLoading={srpResultsLoading}
            />

            {/* Always mounted — removing the isMulticity gate prevents DateSelector
                from remounting (and re-triggering useSearchResults) on tab switch. */}
            <SwipeableDrawer
                        anchor="right"
                        open={fromToDrawerOpen}
                        onClose={() => setFromToDrawerOpen(false)}
                        onOpen={() => setFromToDrawerOpen(true)}
                        slotProps={{
                            paper: {
                                sx: {
                                    width: '100%',
                                    height: '100%'
                                }
                            }
                        }}
                        ModalProps={{
                            keepMounted: false
                        }}
                    >
                        <AirportSelector
                            isPopularAirports={
                                activeComponent === 'from'
                                    ? fromToInputsState.from.isPopularAirports
                                    : fromToInputsState.to.isPopularAirports
                            }
                            type={activeComponent}
                            value={
                                activeComponent === 'from'
                                    ? (fromToInputsState.from.value as string)
                                    : (fromToInputsState.to.value as string)
                            }
                            loading={
                                activeComponent === 'from' ? fromToInputsState.from.loading : fromToInputsState.to.loading
                            }
                            options={
                                activeComponent === 'from'
                                    ? fromToInputsState.from.options
                                    : fromToInputsState.to.options
                            }
                            onSearch={getAirportsFromQuery}
                            onSelect={(value, type) => {
                                onAirportSelect(value, type)
                            }}
                            visible={true}
                            onClose={() => setFromToDrawerOpen(false)}
                        />
                    </SwipeableDrawer>

                    <DateSelector
                        open={dateDrawerOpen}
                        onOpen={() => setDateDrawerOpen(true)}
                        onClose={() => setDateDrawerOpen(false)}
                        isRoundTrip={isRoundTrip}
                        range={range}
                        onDateSelect={rangeDispatch}
                    />

                    <TravelersSelector
                        open={travelersDrawerOpen}
                        onOpen={() => setTravelersDrawerOpen(true)}
                        onClose={() => setTravelersDrawerOpen(false)}
                        adults={travelersState.adults}
                        children={travelersState.childs}
                        infants={travelersState.infants}
                        flightClass={Array.isArray(flightClassState) ? flightClassState[0] : flightClassState}
                        onTravelerChange={handleTravelerChange}
                        onFlightClassChange={handleFlightClassChange}
                        canIncreaseAdults={canIncreaseAdults(travelersState)}
                        canIncreaseChildren={canIncreaseChildren(travelersState)}
                        canIncreaseInfants={canIncreaseInfants(travelersState)}
                        canDecreaseAdults={canDecreaseAdults(travelersState)}
                        canDecreaseChildren={canDecreaseChildren(travelersState)}
                        canDecreaseInfants={canDecreaseInfants(travelersState)}
                        onApply={() => setTravelersDrawerOpen(false)}
                    />
        </>
    )
}

export { SearchHeaderWrapper }
export default SearchHeaderWrapper
